import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Modal,
  Typography,
} from "@mui/material";
import {
  APP_BACKGROUND_COLOR,
  APP_COLOR,
  APP_HOVER_COLOR,
  BORDER_COLOR,
  BUTTON_TEXT_COLOR,
  FONT_FAMILY,
} from "../../theme";
import { acceptEnterpriseAddendum } from "../../services/userService";
import { useState } from "react";

const legalLinkStyle = {
  color: APP_COLOR,
  textDecoration: "none",
  fontWeight: 600,
};

const ConsentModal: React.FC<{
  open: boolean;
  accessToken: string;
  onAccept: () => void;
}> = ({ open, accessToken, onAccept }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const handleAccept = async () => {
    if (!isChecked) return;

    setIsSubmitting(true);
    try {
      await acceptEnterpriseAddendum(accessToken);
    } catch (e) {
      console.error("Failed to record enterprise addendum acceptance:", e);
    } finally {
      setIsSubmitting(false);
      onAccept();
    }
  };

  return (
    <Modal
      open={open}
      disableEscapeKeyDown
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          backgroundColor: APP_BACKGROUND_COLOR,
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: "16px",
          boxShadow: 24,
          width: { xs: "96vw", sm: "620px" },
          maxHeight: "90vh",
          overflowY: "auto",
          padding: { xs: "28px 22px", sm: "32px" },
          fontFamily: FONT_FAMILY,
        }}
      >
        <Typography
          sx={{
            color: "white",
            fontFamily: FONT_FAMILY,
            fontWeight: "bold",
            fontSize: { xs: "24px", sm: "28px" },
            lineHeight: 1.2,
            mb: 2,
          }}
        >
          Review Legal Documents
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.86)",
            fontSize: { xs: "15px", sm: "16px" },
            lineHeight: 1.75,
            mb: 2.5,
          }}
        >
          Please review the documents below before continuing. Your acceptance
          confirms that you understand the terms governing your use of Ommuse
          and how your information is handled.
        </Typography>
        <Box
          sx={{
            mb: 3,
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: "14px",
            backgroundColor: "rgba(255,255,255,0.04)",
            overflow: "hidden",
          }}
        >
          {[
            {
              title: "Terms of Use",
              description:
                "Platform terms, account responsibilities, and permitted use.",
              href: `${window.location.origin}/terms-of-use`,
            },
            {
              title: "Enterprise Addendum",
              description:
                "Additional terms that apply to enterprise access and administration.",
              href: `${window.location.origin}/enterprise-addendum`,
            },
            {
              title: "Privacy Policy",
              description:
                "How Ommuse collects, uses, stores, and protects personal data.",
              href: `${window.location.origin}/privacy-policy`,
            },
          ].map((document, index, documents) => (
            <Box
              key={document.title}
              sx={{
                padding: { xs: "16px", sm: "18px 20px" },
                borderBottom:
                  index < documents.length - 1
                    ? `1px solid ${BORDER_COLOR}`
                    : "none",
              }}
            >
              <Typography
                sx={{
                  color: "white",
                  fontSize: { xs: "16px", sm: "17px" },
                  fontWeight: 700,
                  mb: 0.75,
                }}
              >
                <a
                  href={document.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={legalLinkStyle}
                >
                  {document.title}
                </a>
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.74)",
                  fontSize: { xs: "14px", sm: "15px" },
                  lineHeight: 1.7,
                }}
              >
                {document.description}
              </Typography>
            </Box>
          ))}
        </Box>
        <FormControlLabel
          sx={{
            alignItems: "flex-start",
            mb: 2,
            color: "white",
            "& .MuiFormControlLabel-label": {
              fontSize: "15px",
              lineHeight: 1.65,
            },
          }}
          control={
            <Checkbox
              checked={isChecked}
              onChange={(event) => setIsChecked(event.target.checked)}
              sx={{
                color: APP_COLOR,
                pt: "3px",
                "&.Mui-checked": {
                  color: APP_COLOR,
                },
              }}
            />
          }
          label="I have reviewed the Terms of Use, Enterprise Addendum, and Privacy Policy, and I agree to continue."
        />
        <Button
          fullWidth
          disabled={isSubmitting || !isChecked}
          onClick={handleAccept}
          sx={{
            backgroundColor: APP_COLOR,
            color: BUTTON_TEXT_COLOR,
            fontFamily: FONT_FAMILY,
            fontWeight: "bold",
            textTransform: "none",
            fontSize: "16px",
            borderRadius: "10px",
            padding: "12px",
            "&:hover": {
              backgroundColor: APP_HOVER_COLOR,
            },
            "&.Mui-disabled": {
              color: `${BUTTON_TEXT_COLOR}80`,
              backgroundColor: `${APP_COLOR}60`,
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={20} sx={{ color: BUTTON_TEXT_COLOR }} />
          ) : (
            "Continue"
          )}
        </Button>
      </Box>
    </Modal>
  );
};

export default ConsentModal;
